import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PiCheckBold } from 'react-icons/pi';
import toast from 'react-hot-toast';
import V2Modal from '@v2/components/ui/V2Modal';
import { Button, Input, LoadingState } from '@v2/design-system';
import api from '@v2/lib/api';
import { cn } from '@v2/utils/cn';

type AccessBucket = {
    active: boolean;
    permission_names: string[];
};

type AccessGroup = {
    key: string;
    label: string;
    section: string;
    read: AccessBucket;
    write: AccessBucket;
    delete: AccessBucket;
    export: AccessBucket;
};

type RoleDetail = {
    id: number;
    name: string;
    label: string;
    is_protected: boolean;
    permission_names: string[];
    access_groups: AccessGroup[];
};

type Props = {
    open: boolean;
    mode: 'create' | 'edit';
    roleId: number | null;
    onClose: () => void;
    onSaved?: (roleId: number) => void;
};

type MatrixResponse = {
    access_groups: AccessGroup[];
};

function AccessToggle({
    label,
    active,
    disabled,
    onClick,
}: {
    label: string;
    active: boolean;
    disabled?: boolean;
    onClick: () => void;
}) {
    return (
        <button
            type="button"
            disabled={disabled}
            onClick={onClick}
            className={cn(
                'inline-flex min-w-[4.5rem] items-center justify-center gap-1 rounded-lg border px-3 py-1.5 text-xs font-semibold transition',
                active
                    ? 'border-gray-900 bg-white text-gray-900 shadow-sm dark:border-gray-100 dark:bg-gray-700 dark:text-gray-100'
                    : 'border-gray-200 bg-white text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-400 dark:hover:border-gray-500 dark:hover:text-gray-200',
                disabled && 'cursor-not-allowed opacity-50',
            )}
        >
            {active && <PiCheckBold className="size-3.5" />}
            {label}
        </button>
    );
}

export default function EditRoleModal({ open, mode, roleId, onClose, onSaved }: Props) {
    const qc = useQueryClient();
    const [draftName, setDraftName] = useState('');
    const [permissions, setPermissions] = useState<Set<string>>(new Set());
    const [search, setSearch] = useState('');

    const roleQuery = useQuery<RoleDetail>({
        queryKey: ['roles', roleId],
        enabled: open && mode === 'edit' && roleId != null,
        queryFn: async () => (await api.get(`/roles/${roleId}`)).data,
    });

    const matrixQuery = useQuery<MatrixResponse>({
        queryKey: ['roles', 'matrix'],
        enabled: open && mode === 'create',
        queryFn: async () => (await api.get('/roles/matrix')).data,
    });

    useEffect(() => {
        if (!open) {
            return;
        }
        setSearch('');
        if (mode === 'edit' && roleQuery.data) {
            setDraftName(roleQuery.data.name);
            setPermissions(new Set(roleQuery.data.permission_names));
        }
        if (mode === 'create') {
            setDraftName('');
            setPermissions(new Set());
        }
    }, [open, mode, roleQuery.data]);

    const accessGroups = mode === 'edit' ? (roleQuery.data?.access_groups ?? []) : (matrixQuery.data?.access_groups ?? []);

    const filteredGroups = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) {
            return accessGroups;
        }
        return accessGroups.filter((g) => {
            if (g.label.toLowerCase().includes(q) || g.key.toLowerCase().includes(q)) {
                return true;
            }

            const permissionNames = [
                ...g.read.permission_names,
                ...g.write.permission_names,
                ...g.delete.permission_names,
                ...g.export.permission_names,
            ];

            return permissionNames.some((name) => name.toLowerCase().includes(q));
        });
    }, [accessGroups, search]);

    const isProtected = mode === 'edit' && (roleQuery.data?.is_protected ?? false);
    const isLoading = mode === 'edit' ? roleQuery.isLoading : matrixQuery.isLoading;

    const toggleBucket = (permissionNames: string[]) => {
        if (isProtected || permissionNames.length === 0) {
            return;
        }
        setPermissions((prev) => {
            const next = new Set(prev);
            const allActive = permissionNames.every((n) => prev.has(n));
            permissionNames.forEach((name) => {
                if (allActive) {
                    next.delete(name);
                } else {
                    next.add(name);
                }
            });
            return next;
        });
    };

    const groupsWithState = useMemo(
        () =>
            filteredGroups.map((group) => ({
                ...group,
                read: {
                    ...group.read,
                    active: group.read.permission_names.some((n) => permissions.has(n)),
                },
                write: {
                    ...group.write,
                    active: group.write.permission_names.some((n) => permissions.has(n)),
                },
                delete: {
                    ...group.delete,
                    active: group.delete.permission_names.some((n) => permissions.has(n)),
                },
                export: {
                    ...group.export,
                    active: group.export.permission_names.some((n) => permissions.has(n)),
                },
            })),
        [filteredGroups, permissions],
    );

    const saveMutation = useMutation({
        mutationFn: async () => {
            const payload = {
                name: draftName.trim(),
                permissions: [...permissions],
            };
            if (mode === 'create') {
                return (await api.post('/roles', payload)).data as RoleDetail;
            }
            return (await api.put(`/roles/${roleId}`, payload)).data as RoleDetail;
        },
        onSuccess: (data) => {
            toast.success(mode === 'create' ? 'Função criada.' : 'Permissões salvas.');
            qc.invalidateQueries({ queryKey: ['roles'] });
            qc.invalidateQueries({ queryKey: ['roles', data.id] });
            onSaved?.(data.id);
            onClose();
        },
        onError: (err: { response?: { data?: { message?: string } } }) => {
            toast.error(err?.response?.data?.message ?? 'Não foi possível salvar.');
        },
    });

    const title = mode === 'create' ? 'Nova função' : 'Editar função';

    return (
        <V2Modal
            open={open}
            onClose={onClose}
            title={title}
            size="2xl"
            footer={
                <>
                    <Button type="button" variant="outline" onClick={onClose}>
                        Cancelar
                    </Button>
                    {!isProtected && (
                        <Button
                            type="button"
                            className="bg-gray-900 text-white hover:bg-gray-800"
                            onClick={() => saveMutation.mutate()}
                            disabled={!draftName.trim() || saveMutation.isPending || isLoading}
                        >
                            Salvar
                        </Button>
                    )}
                </>
            }
        >
            {isLoading ? (
                <LoadingState label="Carregando…" />
            ) : (
                <div className="space-y-5">
                    {!isProtected && (
                        <div>
                            <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Nome interno</label>
                            <Input
                                value={draftName}
                                onChange={(e) =>
                                    setDraftName(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))
                                }
                                placeholder="ex.: comex, financeiro"
                            />
                            {mode === 'create' && (
                                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Identificador em minúsculas, sem espaços.</p>
                            )}
                        </div>
                    )}

                    {isProtected && roleQuery.data && (
                        <p className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:bg-amber-950/30 dark:text-amber-300">
                            Função protegida do sistema ({roleQuery.data.label}). Permissões somente leitura.
                        </p>
                    )}

                    <div>
                        <div className="flex items-center justify-between gap-3">
                            <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Acesso da função</h4>
                            <Input
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Filtrar recursos…"
                                className="max-w-xs"
                            />
                        </div>

                                <ul className="mt-3 overflow-hidden rounded-xl border border-gray-200 dark:border-gray-700">
                            {(() => {
                                // Agrupa por seção mantendo a ordem de aparição
                                const sections: { title: string; groups: typeof groupsWithState }[] = [];
                                const seen = new Map<string, number>();
                                for (const g of groupsWithState) {
                                    const sec = g.section ?? 'Outros';
                                    if (!seen.has(sec)) {
                                        seen.set(sec, sections.length);
                                        sections.push({ title: sec, groups: [] });
                                    }
                                    sections[seen.get(sec)!].groups.push(g);
                                }

                                // Paleta de cores por seção (bg header + dot)
                                const SECTION_PALETTE: Record<string, { header: string; dot: string; text: string }> = {
                                    'Intranet':                          { header: 'bg-violet-50 dark:bg-violet-950/30',   dot: 'bg-violet-400',   text: 'text-violet-700 dark:text-violet-300' },
                                    'Comissão — Acompanhamento':         { header: 'bg-sky-50 dark:bg-sky-950/30',         dot: 'bg-sky-400',      text: 'text-sky-700 dark:text-sky-300' },
                                    'Comissão — Configuração (Admin)':   { header: 'bg-indigo-50 dark:bg-indigo-950/30',   dot: 'bg-indigo-400',   text: 'text-indigo-700 dark:text-indigo-300' },
                                    'Comercial / CRM':                   { header: 'bg-amber-50 dark:bg-amber-950/30',     dot: 'bg-amber-400',    text: 'text-amber-700 dark:text-amber-300' },
                                    'Catálogo de Produtos':              { header: 'bg-orange-50 dark:bg-orange-950/30',   dot: 'bg-orange-400',   text: 'text-orange-700 dark:text-orange-300' },
                                    'Operacional — Importação':          { header: 'bg-teal-50 dark:bg-teal-950/30',       dot: 'bg-teal-400',     text: 'text-teal-700 dark:text-teal-300' },
                                    'Parceiros Externos':                { header: 'bg-cyan-50 dark:bg-cyan-950/30',       dot: 'bg-cyan-400',     text: 'text-cyan-700 dark:text-cyan-300' },
                                    'Empresa':                           { header: 'bg-emerald-50 dark:bg-emerald-950/30', dot: 'bg-emerald-400',  text: 'text-emerald-700 dark:text-emerald-300' },
                                    'Administração':                     { header: 'bg-rose-50 dark:bg-rose-950/30',       dot: 'bg-rose-400',     text: 'text-rose-700 dark:text-rose-300' },
                                    'Auditoria':                         { header: 'bg-gray-100 dark:bg-gray-800/60',      dot: 'bg-gray-400',     text: 'text-gray-600 dark:text-gray-400' },
                                };
                                const fallback = { header: 'bg-gray-50 dark:bg-gray-800/40', dot: 'bg-gray-300', text: 'text-gray-500 dark:text-gray-400' };

                                return sections.map((sec) => {
                                    const palette = SECTION_PALETTE[sec.title] ?? fallback;
                                    return (
                                        <li key={sec.title} className="border-b border-gray-200 last:border-0 dark:border-gray-700">
                                            {/* Cabeçalho da seção */}
                                            <div className={`flex items-center gap-2 px-4 py-2 ${palette.header}`}>
                                                <span className={`inline-block h-2 w-2 rounded-full ${palette.dot}`} />
                                                <span className={`text-xs font-bold uppercase tracking-widest ${palette.text}`}>
                                                    {sec.title}
                                                </span>
                                            </div>
                                            {/* Recursos da seção */}
                                            <ul className="divide-y divide-gray-100 dark:divide-gray-700/50">
                                                {sec.groups.map((group) => (
                                                    <li
                                                        key={group.key}
                                                        className="flex flex-wrap items-center justify-between gap-3 px-4 py-3"
                                                    >
                                                        <span className="text-sm text-gray-800 dark:text-gray-200">{group.label}</span>
                                                        <div className="flex flex-wrap gap-2">
                                                            {group.read.permission_names.length > 0 && (
                                                                <AccessToggle
                                                                    label="Ler"
                                                                    active={group.read.active}
                                                                    disabled={isProtected}
                                                                    onClick={() => toggleBucket(group.read.permission_names)}
                                                                />
                                                            )}
                                                            {group.write.permission_names.length > 0 && (
                                                                <AccessToggle
                                                                    label="Escrever"
                                                                    active={group.write.active}
                                                                    disabled={isProtected}
                                                                    onClick={() => toggleBucket(group.write.permission_names)}
                                                                />
                                                            )}
                                                            {group.delete.permission_names.length > 0 && (
                                                                <AccessToggle
                                                                    label="Excluir"
                                                                    active={group.delete.active}
                                                                    disabled={isProtected}
                                                                    onClick={() => toggleBucket(group.delete.permission_names)}
                                                                />
                                                            )}
                                                            {group.export.permission_names.length > 0 && (
                                                                <AccessToggle
                                                                    label="Exportar"
                                                                    active={group.export.active}
                                                                    disabled={isProtected}
                                                                    onClick={() => toggleBucket(group.export.permission_names)}
                                                                />
                                                            )}
                                                        </div>
                                                    </li>
                                            ))}
                                        </ul>
                                        </li>
                                    );
                                });
                            })()}
                        </ul>

                        {groupsWithState.length === 0 && (
                            <p className="py-6 text-center text-sm text-gray-500 dark:text-gray-400">Nenhum recurso encontrado.</p>
                        )}
                    </div>
                </div>
            )}
        </V2Modal>
    );
}
